import {
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from "@heroicons/react/24/solid";
import clsx from "clsx";
import { formatDistanceToNow } from "date-fns";
import mixpanel from "mixpanel-browser";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import {
    EnumDriveDriveType,
    RecentActivityItem,
} from "../__generated__/graphql";
import ButtonLarge from "../components/ButtonLarge";
import AccountDropdown from "../components/Dropdowns/AccountDropdown";
import ActivityDropdown from "../components/Dropdowns/ActivityDropdown";
import {
    FacebookIcon,
    InstagramIcon,
    LinkedinIcon,
    TikTokIcon,
    XIcon,
} from "../components/Sidebar/AppSidebar/SocialIcons";
import useStores from "../hooks/useStores";
import {
    AppsaddIcon,
    Book01Icon,
    ChatIcon,
    File03Icon,
    ImageIcon,
    MonitorIcon,
} from "../icon-imports";
import { PricingPlanTier } from "../models/User";
import {
    activeToolRoute,
    conversationRoute,
    driveRoute,
    editResourceRoute,
    imageGeneratorOutputRoute,
    instantResourcesOutputRoute,
} from "../utils/routeHelper";

const tools = [
  {
    title: "Chat with me",
    description:
      "Your AI teaching assistant for lesson planning, parent communication, IEPs, and daily tasks.",
    path: "/chat",
    color: "sky",
    logo: "/imgs/logos/chat-with-me.png",
  },
  {
    title: "Differentiate It",
    description:
      "Create leveled teaching resources instantly, customized by grade level and language proficiency.",
    path: "/instant-resources",
    color: "green",
    logo: "/imgs/logos/differentiate-it.png",
  },
  {
    title: "Design Curriculum",
    description:
      "Build standards-aligned lesson plans, assessments, and activities with AI-powered guidance.",
    path: "/tools",
    color: "red",
    logo: "/imgs/logos/design-curriculum.png",
  },
  {
    title: "Create Images",
    description:
      "Generate custom educational visuals and artwork to enhance student engagement and learning.",
    path: "/image-generator",
    color: "yellow",
    logo: "/imgs/logos/create-images.png",
  },
];

const resources = [
  {
    title: "PD Deck",
    previewImage: "https://i.ibb.co/YTWqPR7d/pd-deck-cover.png",
    description:
      "Use this to explain how Alayna AI & it's various features to your staff.",
    action: "View Deck",
    path: "https://www.canva.com/design/DAF5XKzIa_o/PLPm0juXs0bBBT_LQCBSSQ/edit?utm_content=DAF5XKzIa_o&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton",
    external: true,
  },
  {
    title: "Free AI Training Course",
    previewImage: "https://i.ibb.co/7JjJ1hKh/free-course-cover.png",
    description: "View our free AI Training Course loved by educators.",
    action: "View Course",
    path: "https://alayna.ai/ai-course-for-teachers",
    external: true,
  },
  {
    title: "Early school partner program",
    previewImage: "https://i.ibb.co/LDdNNG2d/district-parternship-cover.png",
    description:
      "Get discounts on all 4 Alayna AI products and other benefits.",
    action: "Get a Quote",
    path: "https://alayna.ai/for-schools",
    external: true,
  },
];

// Add this helper function
const getActivityUrl = (type: string, url: string) => {
  switch (type) {
    case "Chat":
      return { path: conversationRoute(url) };
    case "InstantResources":
      return { path: instantResourcesOutputRoute(url) };
    case "CurriculumCreator":
      return { path: activeToolRoute(url) };
    case "ImageGenerator":
      return { path: imageGeneratorOutputRoute(url) };
    case "Slides":
      return { path: url, external: true };
    case "Documents":
      return { path: editResourceRoute(url) };
    default:
      return { path: url };
  }
};

function Dashboard() {
  const navigate = useNavigate();

  // const { addModal } = useModalContext();

  const { auth, drives } = useStores();

  const myDriveId = React.useMemo(() => {
    const myDrive = drives.sortedData.find((drive) => {
      return drive.driveType === EnumDriveDriveType.MyDrive;
    });

    if (!myDrive) return null;

    return myDrive.id;
  }, [drives.sortedData]);

  if (!auth.user) return null;

  // const isPaidPlan = auth.user.isSubscribedToPlan;

  // const freeTrialDaysLeft = auth.user.freeTrialDaysLeft;

  const currentPlan = auth.user.currentPlan;

  console.log("Current Plan", currentPlan);

  const recentActivities = toJS(auth.recentActivities);

  // Fetch recent activities on component mount
  React.useEffect(() => {
    auth.getRecentActivityItems();
  }, [auth]);

  // Set up link tracking on mount
  React.useEffect(() => {
    // Track main tool links
    mixpanel.track_links(".tool-link", "tool-clicked", (el: HTMLElement) => ({
      tool_name: el.getAttribute("data-tool-name"),
      tool_path: el.getAttribute("data-tool-path"),
    }));

    // Track resource links
    mixpanel.track_links(
      ".resource-link",
      "resource-clicked",
      (el: HTMLElement) => ({
        resource_name: el.getAttribute("data-resource-name"),
        resource_path: el.getAttribute("data-resource-path"),
      })
    );

    // Track social media links
    mixpanel.track_links(
      ".social-link",
      "social-media-clicked",
      (el: HTMLElement) => ({
        platform: el.getAttribute("data-platform"),
        url: el.getAttribute("href"),
      })
    );
  }, []);

  React.useEffect(() => {
    console.log("Recent Activities", recentActivities);
    // Track activity links
    mixpanel.track_links(
      ".activity-link",
      "activity-clicked",
      (el: HTMLElement) => ({
        activity_type: el.getAttribute("data-activity-type"),
        activity_title: el.getAttribute("data-activity-title"),
      })
    );
  }, [recentActivities]);

  // Helper function to get icon and color based on activity type
  const getActivityMeta = (type: string) => {
    switch (type) {
      case "Chat":
        return { icon: ChatIcon, color: "sky" };
      case "InstantResources":
        return { icon: AppsaddIcon, color: "green" };
      case "CurriculumCreator":
        return { icon: Book01Icon, color: "red" };
      case "ImageGenerator":
        return { icon: ImageIcon, color: "yellow" };
      case "Slides":
        return { icon: MonitorIcon, color: "orange" };
      case "Documents":
        return { icon: File03Icon, color: "red" };
      default:
        return { icon: File03Icon, color: "slate" };
    }
  };

  // Helper function to get friendly name for activity type
  const getActivityTypeName = (type: string) => {
    switch (type) {
      case "Chat":
        return "Chat with me";
      case "InstantResources":
        return "Differentiate It";
      case "CurriculumCreator":
        return "Design Curriculum";
      case "ImageGenerator":
        return "Create Images";
      case "Slides":
        return "Create Slides";
      case "Documents":
        return "Document";
      default:
        return "Resource";
    }
  };

  // Add this component for error state
  const ErrorMessage = () => (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-800">
        {auth.errorFetchingRecentActivities ||
          "Failed to load recent activities"}
      </p>
      <button
        onClick={() => auth.getRecentActivityItems()}
        className="mt-2 text-sm font-medium text-red-600 hover:text-red-800"
      >
        Try again
      </button>
    </div>
  );

  // Add this component for empty state
  const EmptyState = () => (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <File03Icon className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No drafts</h3>
      <p className="text-gray-600">
        Start using our AI tools to see your recent drafts here
      </p>
    </div>
  );

  // Add state to track current slide
  const [currentSlide, setCurrentSlide] = React.useState(0);

  // Custom arrow components
  const NextArrow = ({ onClick }: { onClick?: () => void }) => (
    <button
      onClick={onClick}
      className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white shadow-md hover:bg-gray-100 transition-colors"
      aria-label="Next slides"
    >
      <ChevronRightIcon className="w-5 h-5 text-gray-700" />
    </button>
  );

  const PrevArrow = ({ onClick }: { onClick?: () => void }) => {
    // Don't render if we're at the beginning
    if (currentSlide === 0) return null;

    return (
      <button
        onClick={onClick}
        className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white shadow-md hover:bg-gray-100 transition-colors"
        aria-label="Previous slides"
      >
        <ChevronLeftIcon className="w-5 h-5 text-gray-700" />
      </button>
    );
  };

  // Updated slider settings
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 3,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    beforeChange: (_: number, next: number) => setCurrentSlide(next),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const getActivityIconClasses = (color: string) => {
    return clsx(
      "shrink-0 w-10 h-10 rounded-xl flex items-center justify-center",
      {
        "bg-sky-50": color === "sky",
        "bg-green-50": color === "green",
        "bg-yellow-50": color === "yellow",
        "bg-red-50": color === "red",
        "bg-orange-50": color === "orange",
      }
    );
  };

  const getActivityIconColorClasses = (color: string) => {
    return clsx("w-5 h-5", {
      "text-sky-600": color === "sky",
      "text-green-600": color === "green",
      "text-yellow-600": color === "yellow",
      "text-red-600": color === "red",
      "text-orange-600": color === "orange",
    });
  };

  const getActivityTypeClasses = (color: string) => {
    return clsx("px-2 py-0.5 rounded-full", {
      "bg-sky-50 text-sky-700": color === "sky",
      "bg-green-50 text-green-700": color === "green",
      "bg-yellow-50 text-yellow-700": color === "yellow",
      "bg-red-50 text-red-700": color === "red",
      "bg-orange-50 text-orange-700": color === "orange",
    });
  };

  // Update the ActivityCard component
  const ActivityCard = ({ activity }: { activity: RecentActivityItem }) => {
    const { icon: ActivityIcon, color } = getActivityMeta(activity.type);
    const typeName = getActivityTypeName(activity.type);
    const { path } = getActivityUrl(activity.type, activity.url);

    return (
      <div className="p-2 relative">
        <Link
          to={path}
          data-activity-type={activity.type}
          data-activity-title={activity.title}
          className="activity-link block bg-white rounded-2xl p-6 shadow-sm hover:shadow-sm transition-all border border-gray-200 hover:border-gray-300 h-full"
        >
          <div className="flex gap-4">
            <div className={getActivityIconClasses(color)}>
              <ActivityIcon className={getActivityIconColorClasses(color)} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 mb-1 truncate">
                {activity.title}
              </h3>
              <div className="flex items-center gap-2 text-xs">
                <span className={getActivityTypeClasses(color)}>
                  {typeName}
                </span>
                <span className="text-gray-700">
                  {formatDistanceToNow(new Date(activity.updatedAt), {
                    addSuffix: true,
                  })}
                </span>
              </div>
            </div>
          </div>
        </Link>
        <div className="absolute top-4 right-4">
          <ActivityDropdown
            activityId={activity.id}
            activityType={activity.type}
          />
        </div>
      </div>
    );
  };

  // Update the SkeletonCard component with a custom implementation
  const SkeletonCard = () => (
    <div className="p-2">
      <div className="block bg-white rounded-2xl p-6 shadow-sm border border-gray-200 h-full">
        <div className="flex gap-4">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-gray-200 animate-pulse" />
          <div className="flex-1 min-w-0">
            <div className="h-5 w-3/4 mb-3 bg-gray-200 rounded animate-pulse" />
            <div className="flex items-center gap-2">
              <div className="h-4 w-20 bg-gray-200 rounded-full animate-pulse" />
              <div className="h-4 w-24 bg-gray-200 rounded-full animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#fafafa] max-w-[100vw] overflow-x-hidden">
      {/* Header */}
      <div className="sticky top-0 left-0 right-0 bg-white border-b border-gray-200 z-50">
        <div className="h-18 px-6 md:px-8 flex items-center justify-between max-w-[1600px] mx-auto">
          <div className="flex items-center gap-3">
            <img
              src="/imgs/logos/askalayna.png"
              alt="Ask Alayna Logo"
              className="h-10"
            />
            {currentPlan !== PricingPlanTier.Basic && (
              <span className="px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full">
                PRO
              </span>
            )}
          </div>

          <div className="flex items-center gap-4">
            {!!myDriveId && (
              <ButtonLarge
                buttonText="My Workspace"
                theme="secondary"
                type="button"
                rounded="full"
                onClick={() => navigate(driveRoute(myDriveId))}
              />
            )}
            <AccountDropdown>
              <button className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-full transition-all">
                {auth.user.avatarUrl ? (
                  <img
                    src={auth.user.avatarUrl}
                    alt="User Avatar"
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-medium">
                    {auth.user?.firstName?.charAt(0)}
                    {auth.user?.lastName?.charAt(0)}
                  </div>
                )}
                <ChevronDownIcon className="w-4 h-4 text-gray-600" />
              </button>
            </AccountDropdown>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-16 px-6 md:px-8 max-w-[1600px] mx-auto">
        {/* Recent Activities Carousel */}
        {(auth.isFetchingRecentActivities ||
          auth.recentActivities.length > 0 ||
          auth.errorFetchingRecentActivities) && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Continue where you left off
            </h2>
            <div className="relative pb-3">
              {auth.isFetchingRecentActivities ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                </div>
              ) : auth.errorFetchingRecentActivities ? (
                <ErrorMessage />
              ) : auth.recentActivities.length === 0 ? (
                <EmptyState />
              ) : auth.recentActivities.length > 3 ? (
                <div id="activity-shortcuts">
                  <Slider {...sliderSettings}>
                    {auth.recentActivities.map((activity) => (
                      <ActivityCard key={activity.id} activity={activity} />
                    ))}
                  </Slider>
                </div>
              ) : (
                <div
                  className="grid grid-cols-1 md:grid-cols-3 gap-4"
                  id="activity-shortcuts"
                >
                  {auth.recentActivities.map((activity) => (
                    <ActivityCard key={activity.id} activity={activity} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-8 mb-12">
          {/* Tools Grid */}
          <div className="flex flex-col gap-4">
            <h2 className="text-2xl font-bold text-gray-900">
              Pick your AI tool
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
              {tools.map((tool) => (
                <Link
                  key={tool.title}
                  to={tool.path}
                  className="tool-link group block h-full"
                  data-tool-name={tool.title}
                  data-tool-path={tool.path}
                >
                  <div className="bg-white p-6 xl:p-8 rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all h-full">
                    <div className="flex flex-col items-start gap-6 h-full">
                      <img
                        src={tool.logo}
                        alt={tool.title}
                        className="w-16 h-16 shrink-0"
                      />
                      <div className="flex flex-col min-h-0">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-3">
                          {tool.title}
                        </h2>
                        <p className="text-base text-gray-600">
                          {tool.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Resources Column */}
          <div className="flex flex-col gap-4 w-full">
            <h2 className="text-2xl font-bold text-gray-900">Resources</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-4 gap-6">
              {resources.map((resource) => (
                <Link
                  key={resource.title}
                  to={resource.path}
                  target={resource.external ? "_blank" : undefined}
                  rel={resource.external ? "noopener noreferrer" : undefined}
                  className="resource-link group block h-full"
                  data-resource-name={resource.title}
                  data-resource-path={resource.path}
                >
                  <div className="w-full rounded-2xl bg-white border overflow-hidden border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all h-full">
                    <div className="flex flex-col gap-2 h-full">
                      {resource.previewImage && (
                        <div className="max-h-[280px] rounded-t-lg overflow-hidden h-full">
                          <img
                            src={resource.previewImage}
                            alt={resource.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="w-full flex flex-col justify-between p-6">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            {resource.title}
                          </h3>
                          <p className="text-gray-600 mb-3">
                            {resource.description}
                          </p>
                        </div>
                        <span className="text-sm font-medium text-[#0090FF] hover:text-blue-700 flex items-center gap-1 self-start">
                          {resource.action}
                          <span className="transition-transform group-hover:animate-arrow-right">
                            →
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-16">
        <div className="max-w-[1600px] mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand Column */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img
                  src="/imgs/logos/askalayna.png"
                  alt="Ask Alayna Logo"
                  className="h-12"
                />
              </div>
              <p className="text-gray-600 text-sm">
                Simple, yet powerful AI teaching assistant for educators
              </p>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Company</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="https://alayna.ai/about"
                    target="_blank"
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to="https://alayna.ai/blog"
                    target="_blank"
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    Blog
                  </Link>
                </li>
                <li>
                  <Link
                    to="https://calendly.com/paul-gradingpal/30min"
                    target="_blank"
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources Links */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Resources</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="https://alayna.ai/use-cases"
                    target="_blank"
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    Use Cases
                  </Link>
                </li>
                <li>
                  <Link
                    to="https://alayna.ai/pricing"
                    target="_blank"
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    Pricing
                  </Link>
                </li>
                <li>
                  <Link
                    to="https://www.canva.com/design/DAF5XKzIa_o/PLPm0juXs0bBBT_LQCBSSQ/edit?utm_content=DAF5XKzIa_o&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton"
                    target="_blank"
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    PD Deck
                  </Link>
                </li>
                <li>
                  <Link
                    to="https://alayna.featurebase.app/"
                    target="_blank"
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    Feedback Forum
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="https://alayna.ai/terms"
                    target="_blank"
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="https://alayna.ai/privacy"
                    target="_blank"
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="https://workspace.google.com/marketplace/app/alayna_ai_for_google_slides/503455951279"
                    target="_blank"
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    Install Slides Extension
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 text-sm">
              © {new Date().getFullYear()} Cues Technologies Inc. All rights
              reserved.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://www.facebook.com/profile.php?id=61564964300303"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link text-gray-400 hover:text-blue-600 transition-colors"
                data-platform="facebook"
                aria-label="Facebook"
              >
                <FacebookIcon />
              </a>
              <a
                href="https://x.com/joinalayna"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link text-gray-400 hover:text-blue-600 transition-colors"
                data-platform="twitter"
                aria-label="X (formerly Twitter)"
              >
                <XIcon />
              </a>
              <a
                href="https://www.instagram.com/joinalayna/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link text-gray-400 hover:text-blue-600 transition-colors"
                data-platform="instagram"
                aria-label="Instagram"
              >
                <InstagramIcon />
              </a>
              <a
                href="https://www.tiktok.com/@joinalayna"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link text-gray-400 hover:text-blue-600 transition-colors"
                data-platform="tiktok"
                aria-label="TikTok"
              >
                <TikTokIcon />
              </a>
              <a
                href="https://www.linkedin.com/company/alayna"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link text-gray-400 hover:text-blue-600 transition-colors"
                data-platform="linkedin"
                aria-label="LinkedIn"
              >
                <LinkedinIcon />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default observer(Dashboard);

